// src/components/admin/AdminSidebar.js
import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Avatar,
  Tooltip,
  Chip,
  Badge,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
  Assignment as AssignmentIcon,
  Groups as GroupsIcon,
  CreditCard as CreditCardIcon,
  SupportAgent as SupportAgentIcon,
  Layers as LayersIcon,
  Backup as BackupIcon,
  SupervisorAccount as SupervisorAccountIcon,
  BarChart as BarChartIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { themeColors } from "../../theme/colors";
import logo from "../../Assets/logo.png";

const AdminSidebar = ({
  mobileOpen,
  handleDrawerToggle,
  desktopOpen,
  handleDesktopToggle,
  drawerWidth,
  collapsedWidth,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    // توجه إلى صفحة تسجيل الدخول عبر تغيير href — هذا يضمن إعادة تحميل المتصفح (ريفريش)
    window.location.href = "/login";
  };

  const menuItems = [
    { text: "الخطط", icon: <AssignmentIcon />, path: "/admin/plans" },
    { text: "العملاء", icon: <GroupsIcon />, path: "/admin/customers" },
    { text: "الاشتراكات", icon: <CreditCardIcon />, path: "/admin/subscriptions" },
    { text: "جميع النسخ", icon: <LayersIcon />, path: "/admin/versions" },
    { text: "النسخ الاحتياطي", icon: <BackupIcon />, path: "/admin/backup" },
    { text: "الموزعين", icon: <PeopleIcon />, path: "/admin/brokers" },
    { text: "الدعم الفني", icon: <SupportAgentIcon />, path: "/admin/support" },
    { text: "لوحة تحكم الموقع", icon: <LanguageIcon />, path: "/admin/websitedashboard" },
    { text: "المستخدمين", icon: <SupervisorAccountIcon />, path: "/admin/users" },
    { text: "التقارير", icon: <BarChartIcon />, path: "/admin/reports" },
    { text: "طلبات التواصل", icon: <SupportAgentIcon />, path: "/admin/contact-requests" },
  ];

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(180deg, ${themeColors.background.sidebar} 0%, #1a1f2e 100%)`,
        color: themeColors.text.white,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          background: "radial-gradient(circle at top right, rgba(255,107,53,0.1) 0%, transparent 50%)",
          pointerEvents: "none",
        },
      }}
    >
      {/* Header with Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: desktopOpen ? "space-between" : "center",
          padding: desktopOpen ? "24px 20px" : "24px 12px",
          minHeight: "90px",
          borderBottom: `1px solid rgba(255,255,255,0.08)`,
          backdropFilter: "blur(10px)",
          background: "rgba(255,255,255,0.02)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {desktopOpen ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: -4,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${themeColors.primary.main}, ${themeColors.primary.dark})`,
                  opacity: 0.2,
                  filter: "blur(8px)",
                },
              }}
            >
              <Avatar
                src={logo}
                alt="RestoCash Logo"
                sx={{
                  width: 50,
                  height: 50,
                  backgroundColor: themeColors.background.paper,
                  padding: "5px",
                  boxShadow: `0 4px 12px ${themeColors.primary.main}40`,
                  border: `2px solid ${themeColors.primary.main}30`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05) rotate(5deg)",
                    boxShadow: `0 6px 16px ${themeColors.primary.main}60`,
                  },
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${themeColors.primary.main}, ${themeColors.primary.light})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "0.5px",
                  textShadow: `0 2px 10px ${themeColors.primary.main}40`,
                }}
              >
                RestoCash
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="caption"
                  sx={{
                    color: themeColors.text.light,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                  }}
                >
                  لوحة تحكم الأدمن
                </Typography>
                <Chip
                  label="Administration"
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${themeColors.primary.main}, ${themeColors.primary.dark})`,
                    color: "white",
                    "& .MuiChip-label": { px: 0.8 },
                  }}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: -4,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${themeColors.primary.main}, ${themeColors.primary.dark})`,
                opacity: 0.2,
                filter: "blur(8px)",
              },
            }}
          >
            <Avatar
              src={logo}
              alt="RestoCash"
              sx={{
                width: 45,
                height: 45,
                backgroundColor: themeColors.background.paper,
                padding: "4px",
                boxShadow: `0 4px 12px ${themeColors.primary.main}40`,
                border: `2px solid ${themeColors.primary.main}30`,
              }}
            />
          </Box>
        )}
      </Box>

      {/* Menu Items */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          px: 1.5,
          pt: 2,
          position: "relative",
          zIndex: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255,255,255,0.05)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: `linear-gradient(180deg, ${themeColors.primary.main}, ${themeColors.primary.dark})`,
            borderRadius: "10px",
            "&:hover": {
              background: themeColors.primary.main,
            },
          },
        }}
      >
        <List sx={{ p: 0 }}>
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <Tooltip
                key={item.path}
                title={!desktopOpen ? item.text : ""}
                placement="left"
                arrow
              >
                <ListItem
                  button
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 2.5,
                    mb: 1,
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: isActive
                      ? `${themeColors.primary.main}20`
                      : "transparent",
                    color: isActive
                      ? themeColors.primary.main
                      : themeColors.text.light,
                    justifyContent: desktopOpen ? "flex-start" : "center",
                    px: desktopOpen ? 2.5 : 1.5,
                    py: 1.8,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    border: isActive
                      ? `1px solid ${themeColors.primary.main}40`
                      : "1px solid transparent",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: isActive ? "4px" : "0",
                      background: `linear-gradient(180deg, ${themeColors.primary.main}, ${themeColors.primary.dark})`,
                      transition: "width 0.3s ease",
                      borderRadius: "0 4px 4px 0",
                    },
                    "&:hover": {
                      backgroundColor: isActive
                        ? `${themeColors.primary.main}30`
                        : "rgba(255,255,255,0.08)",
                      color: themeColors.text.white,
                      transform: "translateX(-6px)",
                      boxShadow: isActive
                        ? `0 4px 12px ${themeColors.primary.main}40`
                        : "0 2px 8px rgba(0,0,0,0.2)",
                      "&::before": {
                        width: "4px",
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "inherit",
                      minWidth: desktopOpen ? 45 : "auto",
                      justifyContent: "center",
                      transition: "transform 0.3s ease",
                      ".MuiListItem-root:hover &": {
                        transform: "scale(1.1) rotate(5deg)",
                      },
                    }}
                  >
                    {isActive ? (
                      <Badge
                        variant="dot"
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: themeColors.primary.main,
                            boxShadow: `0 0 0 2px ${themeColors.background.sidebar}`,
                          },
                        }}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  {desktopOpen && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: "0.95rem",
                        fontWeight: isActive ? 700 : 500,
                        letterSpacing: "0.3px",
                      }}
                    />
                  )}
                  {isActive && desktopOpen && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: themeColors.primary.main,
                        boxShadow: `0 0 8px ${themeColors.primary.main}`,
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%, 100%": {
                            opacity: 1,
                            transform: "scale(1)",
                          },
                          "50%": {
                            opacity: 0.6,
                            transform: "scale(1.2)",
                          },
                        },
                      }}
                    />
                  )}
                </ListItem>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Divider
        sx={{
          borderColor: "rgba(255,255,255,0.08)",
          mx: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />

      {/* Logout Button */}
      <Box sx={{ px: 1.5, py: 2, position: "relative", zIndex: 1 }}>
        <Tooltip title={!desktopOpen ? "تسجيل الخروج" : ""} placement="left" arrow>
          <ListItem
            button
            onClick={handleLogout}
            sx={{
              borderRadius: 2.5,
              color: themeColors.text.light,
              justifyContent: desktopOpen ? "flex-start" : "center",
              px: desktopOpen ? 2.5 : 1.5,
              py: 1.8,
              border: "1px solid transparent",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: "rgba(220, 53, 69, 0.15)",
                color: themeColors.error,
                transform: "translateX(-6px)",
                border: `1px solid ${themeColors.error}40`,
                boxShadow: `0 4px 12px ${themeColors.error}30`,
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "inherit",
                minWidth: desktopOpen ? 45 : "auto",
                justifyContent: "center",
                transition: "transform 0.3s ease",
                ".MuiListItem-root:hover &": {
                  transform: "scale(1.1)",
                },
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {desktopOpen && (
              <ListItemText
                primary="تسجيل الخروج"
                primaryTypographyProps={{
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  letterSpacing: "0.3px",
                }}
              />
            )}
          </ListItem>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <nav>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxShadow: "0 0 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        anchor="right"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: desktopOpen ? drawerWidth : collapsedWidth,
            overflowX: "hidden",
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "-4px 0 12px rgba(0,0,0,0.08)",
            border: "none",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </nav>
  );
};

export default AdminSidebar;
